import { UpdateProfileReq, User } from '~/types/ResponseType'
import { fetchWrapper } from '~/utils/fetchWrapper'
import { getDeviceId } from '~/utils/storage'

export const signup = async (
  email: string,
  password: string,
  code: string,
  fromCampaign: boolean = false
) => {
  const response = await fetch('/api/user/signup', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({
      email,
      password,
      code,
      from_campaign: fromCampaign
    })
  })
  if (!response.ok) {
    const responeJson = await response.json()
    const reason = responeJson.detail
    throw new Error(`Failed to signup. ${reason}`)
  }
  const data = await response.json()
  return data
}

export const joinWaitlist = async (email: string) => {
  const response = await fetch('/api/campaign/join-waitlist', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({
      uid: getDeviceId(),
      email
    })
  })
  if (!response.ok) {
    const responeJson = await response.json()
    const reason = responeJson.detail
    throw new Error(reason)
  }
}

export const sendVerificationCode = async (email: string) => {
  const response = await fetch('/api/v1/user/email/send-verification', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({ email })
  })
  if (!response.ok) {
    const responseJson = await response.json()
    const reason = responseJson.detail[0].msg
    throw new Error(`Failed to send verification code. ${reason}`)
  }
  return (await response.json()) as { referer: string }
}

export const sendResetPasswordVerification = async (email: string) => {
  const response = await fetch(`/api/v1/user/email/reset-password`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({ email })
  })
  if (!response.ok) {
    const responseJson = await response.json()
    const reason = responseJson.detail
    throw new Error(`Failed to send reset password verification. ${reason}`)
  }
}

export const signin = async (email: string, password: string, mfa_code: string | null) => {
  const response = await fetch('/api/user/signin', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({ email, password, mfa_code })
  })
  if (!response.ok) {
    const responseJson = await response.json()
    const reason = responseJson.detail
    throw new Error(`Failed to signin. ${reason}`)
  }
  const data = await response.json()
  if ('email_verified' in data && !data.email_verified) {
    return { email_verified: false, referer: null }
  } else if ('onboarded' in data && !data.onboarded) {
    return { onboarded: false, referer: null }
  } else if (data.mfa_enabled) {
    return { mfa_required: true, referer: null }
  }

  return data as { referer: string }
}

export const ssoSignin = async () => {
  const response = await fetch('/sso/microsoft/login', {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json'
    }
  })
  if (!response.ok) {
    const responseJson = await response.json()
    const reason = responseJson.detail
    throw new Error(`Failed to signin. ${reason}`)
  }
}

export const getUserProfile = async (): Promise<User> => {
  const headers = new Headers({ 'Content-Type': 'application/json' })
  const response = await fetchWrapper(`/api/user/profile`, { headers })
  const data = await response.json()
  return data
}

export const updateUserProfile = async (user: Partial<UpdateProfileReq>) => {
  const headers = new Headers({ 'Content-Type': 'application/json' })
  const response = await fetchWrapper(`/api/user/profile`, {
    method: 'POST',
    headers,
    body: JSON.stringify(user)
  })
  if (response.status !== 200) {
    const json = await response.json()
    if (response.status !== 200) {
      throw new Error(json.detail)
    }
  }
  const data = await response.json()
  return data
}

export const deleteAccount = async () => {
  const headers = new Headers({ 'Content-Type': 'application/json' })
  const response = await fetchWrapper(`/api/user/delete-account`, { headers })

  const json = await response.json()
  if (response.status !== 200) {
    throw new Error(json.detail)
  }
  return json.detail
}

export const emailverify = async (email: string, code: string) => {
  const headers = new Headers({ 'Content-Type': 'application/json' })
  const response = await fetchWrapper(`/api/user/email/verify`, {
    method: 'POST',
    headers,
    body: JSON.stringify({ email, code })
  })

  const json = await response.json()
  if (response.status !== 200) {
    throw new Error(json.detail)
  }
  return json.detail
}

export const resetPassword = async (
  email: string,
  verificationCode: string,
  newPassword: string
) => {
  const headers = new Headers({ 'Content-Type': 'application/json' })
  const response = await fetch(`/api/v1/user/email/verify-reset-password`, {
    method: 'POST',
    headers,
    body: JSON.stringify({
      email,
      code: verificationCode,
      password: newPassword
    })
  })

  const message = await response.json()
  if (response.status !== 200) {
    throw new Error(message.detail)
  }
  return message.detail
}

export const emailCodeVerification = async (email: string, code: string) => {
  const response = await fetch('/api/v1/user/email/verify-email-code', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({ email, code })
  })
  if (!response.ok) {
    const responseJson = await response.json()
    const reason = responseJson.detail
    throw new Error(`Failed to verify email. ${reason}`)
  }
  const data = await response.json()
  return data
}

export const signupUnverified = async (email: string, password: string) => {
  const response = await fetch('/api/user/signup/unverified', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({ email, password })
  })
  if (!response.ok) {
    const responeJson = await response.json()
    const reason = responeJson.detail
    throw new Error(`Failed to signup unverified email. ${reason}`)
  }
  const data = await response.json()
  return data
}

export const signupVerifyEmail = async (email: string, code: string) => {
  const response = await fetch('/api/user/signup/verify-email', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({ email, password: null, code })
  })
  if (!response.ok) {
    const responeJson = await response.json()
    const reason = responeJson.detail
    throw new Error(`Failed to verify email. ${reason}`)
  }
  const data = await response.json()
  return data
}

export const checkEmailVerificationStatus = async (email: string) => {
  const response = await fetch('/api/user/verify-email-status', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({ email })
  })

  if (!response.ok) {
    const responseJson = await response.json()
    const reason = responseJson.detail
    throw new Error(`Failed to check email status. ${reason}`)
  }

  const data = await response.json()
  return data.email_verified
}

export interface Session {
  device: string
  last_active: string
  is_current_device: boolean
}

interface SessionsResponse {
  sessions: Session[]
}

export const getSessions = async (): Promise<SessionsResponse> => {
  const headers = new Headers({ 'Content-Type': 'application/json' })
  const response = await fetchWrapper('/api/user/sessions', { headers })

  if (!response.ok) {
    const responseJson = await response.json()
    const reason = responseJson.detail
    throw new Error(`Failed to get sessions. ${reason}`)
  }

  const data = await response.json()
  return data
}

export const deleteSession = async (deviceName: string): Promise<void> => {
  const headers = new Headers({ 'Content-Type': 'application/json' })
  const response = await fetchWrapper('/api/user/sessions', {
    method: 'DELETE',
    headers,
    body: JSON.stringify({ device_name: deviceName })
  })

  if (!response.ok) {
    const responseJson = await response.json()
    const reason = responseJson.detail
    throw new Error(`Failed to delete session. ${reason}`)
  }
}

export const getAccessLogs = async () => {
  const headers = new Headers({ 'Content-Type': 'application/json' })
  const response = await fetchWrapper('/api/user/access-logs', { headers })

  if (!response.ok) {
    const responseJson = await response.json()
    const reason = responseJson.detail
    throw new Error(`Failed to get access logs. ${reason}`)
  }

  const data = await response.json()
  return data
}
